import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoNotificationsOutline } from 'react-icons/io5';
import { FaUserCircle, FaEnvelope, FaBell, FaSignOutAlt, FaHome } from 'react-icons/fa';
import './Header.css';
import logo from '../../assets/images/logo.png';
import { getMyNotifications } from '../../api/services/notificationService';
import { getNotificationSideForUserRole } from '../../utils/notificationSide';
import { logoutUser, fetchAuthenticatedImage, getProfile } from '../../api/services/authService';
import ProfileAvatar from '../ProfileAvatarContent/ProfileAvatar';

const Header = ({ userRole, activeSection, onNavClick }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarToggleRef = useRef(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);

  const notificationSide = useMemo(
    () => getNotificationSideForUserRole(userRole),
    [userRole]
  );

  useEffect(() => {
    if (userRole === 'System Admin') return;

    let mounted = true;
    
    const fetchUnreadCount = async () => {
      try {
        const res = await getMyNotifications(0, 40, notificationSide);
        if (mounted && res && res.data) {
          const rawContent = res.data.content || [];
          
          // Count unread items (readAt is null) after simple deduplication
          const deduplicated = rawContent.filter((notif, index, self) => {
            return index === self.findIndex((n) => (
              n.type === notif.type &&
              n.title === notif.title &&
              n.body === notif.body &&
              Math.abs(new Date(n.createdAt).getTime() - new Date(notif.createdAt).getTime()) < 5000
            ));
          });
          
          const unread = deduplicated.filter(n => !n.readAt).length;
          setUnreadCount(unread);
        }
      } catch (e) {
        console.error('Failed to fetch unread count:', e);
      }
    };

    fetchUnreadCount();

    const handleNewNotification = () => {
      setTimeout(fetchUnreadCount, 800);
    };

    const handleNotificationDeleted = () => {
      fetchUnreadCount();
    };

    window.addEventListener('app:new_notification', handleNewNotification);
    window.addEventListener('app:notification_deleted', handleNotificationDeleted);

    return () => {
      mounted = false;
      window.removeEventListener('app:new_notification', handleNewNotification);
      window.removeEventListener('app:notification_deleted', handleNotificationDeleted);
    };
  }, [userRole, notificationSide]);

  useEffect(() => {
    if (userRole !== 'Tenant') return;

    let mounted = true;
    setProfileLoading(true);

    const load = async () => {
      try {
        const profileRes = await getProfile();
        const data = profileRes?.data || profileRes;
        if (!mounted) return;
        setProfile(data || null);

        const photoPath = data?.profilePhotoPath || data?.profilePhotoUrl || '';
        if (photoPath) {
          const blobUrl = await fetchAuthenticatedImage(photoPath);
          if (mounted) setProfileAvatarUrl(blobUrl || null);
        } else {
          setProfileAvatarUrl(null);
        }
      } catch (e) {
        console.error('Failed to load profile for header:', e);
        if (mounted) {
          setProfile(null);
          setProfileAvatarUrl(null);
        }
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userRole]);

  useEffect(() => {
    const handleToggle = (e) => {
      setSidebarOpen(!!e.detail);
    };
    window.addEventListener('toggleSidebar', handleToggle);
    return () => window.removeEventListener('toggleSidebar', handleToggle);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const handleDocumentClick = (e) => {
      const container = document.querySelector('.user-avatar-container');
      if (container && !container.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      window.scrollTo(0, scrollY);
    };
  }, [userMenuOpen]);

  const handleNavClick = (section) => {
    if (onNavClick) {
      onNavClick(section);
    }
  };

  const toggleSidebar = (e) => {
    if (sidebarToggleRef.current) {
      return;
    }
    
    sidebarToggleRef.current = true;
    
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    window.dispatchEvent(new CustomEvent('toggleSidebar', { detail: newState }));
    
    setTimeout(() => {
      sidebarToggleRef.current = false;
    }, 300);
  };

  const displayName = useMemo(() => {
    if (userRole !== 'Tenant') return '';
    const first = profile?.firstName || '';
    const last = profile?.lastName || '';
    const combined = `${first} ${last}`.trim();
    return combined || profile?.email || 'Tenant';
  }, [profile, userRole]);

  return (
    <header className="header">
      <div className="header-left">
        {userRole !== 'Tenant' && (
          <button 
            className="mobile-menu-btn"
            onClick={toggleSidebar}
            aria-label="Toggle menu"
            type="button"
          >
            ☰
          </button>
        )}
        <div className="logo-container">
          <img src={logo} alt="TenantScore Logo" className="logo-image" />
        </div>
      </div>
      <div className="header-right">
        {userRole !== 'System Admin' && userRole !== 'Tenant' && (
          <button 
            type="button" 
            className="header-icon-btn notification-btn-header"
            onClick={() => {
              setUnreadCount(0); // Optimistic clear
              handleNavClick('notifications');
            }}
            aria-label="Notifications"
          >
            <IoNotificationsOutline />
            {unreadCount > 0 && <span className="notification-badge"></span>}
          </button>
        )}

        {userRole === 'Tenant' && (
          <div className="user-avatar-container">
            <button
              type="button"
              className="user-avatar-btn"
              aria-label="Open My Profile menu"
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <ProfileAvatar
                variant="header"
                imageUrl={profileAvatarUrl}
                profile={profile}
                title="My Profile"
              />
              <span className="user-avatar-name">My Profile</span>
            </button>

            {userMenuOpen && (
              <>
                <div className="mobile-user-menu-overlay" onClick={() => setUserMenuOpen(false)} />
                <div className="mobile-user-menu-panel" role="menu" aria-label="User menu">
                  <button
                    type="button"
                    className="mobile-user-menu-close"
                    onClick={() => setUserMenuOpen(false)}
                    aria-label="Close user menu"
                  >
                    ×
                  </button>

                  <div className="mobile-user-menu-profile">
                    <ProfileAvatar variant="menu" imageUrl={profileAvatarUrl} profile={profile} imgAlt="" />
                    <div className="mobile-user-menu-text">
                      <div className="mobile-user-menu-name">{displayName}</div>
                      <div className="mobile-user-menu-role">Tenant</div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="mobile-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/listings');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaHome /></span>
                    Home
                  </button>
                  <button
                    type="button"
                    className="mobile-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/tenant/account');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaUserCircle /></span>
                    My account
                  </button>
                  <button
                    type="button"
                    className="mobile-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/tenant/messages');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaEnvelope /></span>
                    Messages
                  </button>
                  <button
                    type="button"
                    className="mobile-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setUnreadCount(0);
                      navigate('/tenant/notifications');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaBell /></span>
                    Notifications
                    {unreadCount > 0 ? <span className="nav-badge" style={{ marginLeft: 'auto' }}>{unreadCount}</span> : null}
                  </button>
                  <button
                    type="button"
                    className="mobile-user-menu-item mobile-user-menu-item-danger"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logoutUser();
                      navigate('/login');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaSignOutAlt /></span>
                    Sign out
                  </button>
                </div>

                <div className="user-dropdown" role="menu" aria-label="User menu">
                  {profileLoading ? (
                    <div className="profile-loading">Loading...</div>
                  ) : (
                    <div className="profile-info">
                      <div className="profile-header">
                        <ProfileAvatar variant="dropdown" imageUrl={profileAvatarUrl} profile={profile} imgAlt="" />
                        <div className="profile-name">
                          <div className="profile-full-name">{displayName}</div>
                          <div className="profile-role-line">Tenant</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button type="button" className="dropdown-item" onClick={() => navigate('/listings')}>
                    <span className="dropdown-icon" aria-hidden="true"><FaHome /></span>
                    Home
                  </button>
                  <button type="button" className="dropdown-item" onClick={() => navigate('/tenant/account')}>
                    <span className="dropdown-icon" aria-hidden="true"><FaUserCircle /></span>
                    My account
                  </button>
                  <button type="button" className="dropdown-item" onClick={() => navigate('/tenant/messages')}>
                    <span className="dropdown-icon" aria-hidden="true"><FaEnvelope /></span>
                    Messages
                  </button>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setUnreadCount(0);
                      navigate('/tenant/notifications');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaBell /></span>
                    Notifications
                    {unreadCount > 0 ? <span className="nav-badge" style={{ marginLeft: 'auto' }}>{unreadCount}</span> : null}
                  </button>
                  <button
                    type="button"
                    className="dropdown-item dropdown-item-danger"
                    onClick={() => {
                      logoutUser();
                      navigate('/login');
                    }}
                  >
                    <span className="dropdown-icon" aria-hidden="true"><FaSignOutAlt /></span>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
